/**
 * RTP Performers Management Page
 * Main component for managing RTP Performers with filtering and verification capabilities
 * Features:
 * - Performer grid display with filtering capabilities
 * - Bulk selection and status management (Active/Inactive) and verification (Whitelist/Blacklist)
 * - Sync functionality for updating performer data from VividSkybox API
 */
import React, { useState, useRef } from 'react';
import { Chip } from '@mui/material';
import RaList from '../../../components/ra-list/RaList';
import RenderControls from '../../../components/ra-list/RenderControls';
import {
    DatagridConfigurable,
    TextField,
    NumberField,
    FunctionField,
} from 'react-admin';
import { formatLocalTime } from '../../../components/ex_dayjs';
import VerificationActions from '../components/VerificationActions';
import { reloadPage } from '../../../utils/common';
import type {
    RTPPerformer,
    FilterControl,
    FilterChoice,
    DefaultPerformerFilters
} from './types';

/**
 * Main component for RTP Performers management page
 * Provides interface for viewing, managing, and syncing event performers from VividSkybox API
 * Features bulk selection and status management (Active/Inactive) and verification (Whitelist/Blacklist)
 * @returns {JSX.Element} RTP Performers list component
 */
const RTP_Performers: React.FC = () => {
    const refbtnFilter = useRef<HTMLElement>(null);
    const [defaultSort] = useState<string[]>(['id asc']);
    const [defaultValues] = useState<DefaultPerformerFilters>({});

    /**
     * Reloads the main page data by triggering filter refresh
     */
    const reloadMainPage = (): void => {
        reloadPage('RTP_Performers', refbtnFilter);
    };

    // Status options for filtering
    const statusOptions: FilterChoice[] = [
        { id: 0, name: 'Inactive' },
        { id: 1, name: 'Active' }
    ];

    // Verification status options for filtering
    const verificationStatusOptions: FilterChoice[] = [
        { id: 0, name: 'Not Verified' },
        { id: 1, name: 'Whitelist' },
        { id: 2, name: 'Blacklist' }
    ];

    // Filter controls configuration
    const controls: FilterControl[] = [
        { component: 'text', name: 'name', label: 'Name', xs: 2 },
        { component: 'text', name: 'eventType', label: 'Event Type', xs: 2 },
        { component: 'number', name: 'category_id', label: 'Category ID', xs: 1 },
        { component: 'select', name: 'status', label: 'Status', choices: statusOptions, xs: 1 },
        { component: 'select', name: 'verification_status_eq', label: 'Verification', choices: verificationStatusOptions, xs: 1 }
    ];

    /**
     * Renders status chip based on performer status value
     * @param record - The performer record containing status information
     * @returns {JSX.Element} Status chip component
     */
    const renderStatusChip = (record: RTPPerformer): JSX.Element => (
        <Chip
            label={record.status === 1 ? 'Active' : 'Inactive'}
            color={record.status === 1 ? 'success' : 'default'}
            size="small"
        />
    );

    /**
     * Gets verification configuration for status rendering
     * @param status - The verification status value
     * @returns {object} Configuration object with label and color
     */
    const getVerificationConfig = (status: number): { label: string; color: 'default' | 'success' | 'error' } => {
        switch (status) {
            case 0:
                return { label: 'Not Verified', color: 'default' };
            case 1:
                return { label: 'Whitelist', color: 'success' };
            case 2:
                return { label: 'Blacklist', color: 'error' };
            default:
            // Log unexpected verification status values for debugging
                console.warn(`Unexpected verification_status value: ${status}`);
                return { label: 'Unknown', color: 'default' };
        }
    };

    /**
     * Renders verification status chip based on verification status value
     * @param record - The performer record containing verification status information
     * @returns {JSX.Element} Verification status chip component
     */
    const renderVerificationChip = (record: RTPPerformer): JSX.Element => {
        const config = getVerificationConfig(record.verification_status);
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
            />
        );
    };

    /**
     * Renders formatted date string for created field
     * @param record - The performer record containing created date information
     * @returns {string} Formatted date string or empty string
     */
    const renderCreatedDate = (record: RTPPerformer): any =>
        record.created ? formatLocalTime(record.created) : '';

    /**
     * Renders formatted date string for modified field
     * @param record - The performer record containing modified date information
     * @returns {string} Formatted date string or empty string
     */
    const renderModifiedDate = (record: RTPPerformer): any =>
        record.modified ? formatLocalTime(record.modified) : '';

    return (
        <>
            <RaList
                sortby={defaultSort}
                perPage={25}
                title={'Event Performers Management'}
                storeKey={true}
                filters={
                    <RenderControls
                        controls={controls}
                        refFilter={refbtnFilter}
                        defaultValues={defaultValues}
                        // actions={<SyncButton reload={reloadMainPage} />}
                        exportFile={{
                            filename: `RTP-Performers-${formatLocalTime(new Date())}`,
                            columns: ['id', 'name', 'eventType', 'category_id', 'status', 'verification_status', 'created', 'modified'],
                            limit: 1000000
                        }}
                    />
                }
            >
                <DatagridConfigurable
                    bulkActionButtons={<VerificationActions display={true} reload={reloadMainPage} action='Update' entityType="Performers" />}
                >
                    <TextField source="id" label="ID" />
                    <TextField source="name" label="Name" />
                    <TextField source="eventType" label="Event Type" />
                    <NumberField source="category_id" label="Category ID" />
                    <FunctionField
                        source="status"
                        label="Status"
                        render={renderStatusChip}
                    />
                    <FunctionField
                        source="verification_status"
                        label="Verification Status"
                        render={renderVerificationChip}
                    />
                    <FunctionField
                        source="created"
                        label="Created"
                        render={renderCreatedDate}
                    />
                    <FunctionField
                        source="modified"
                        label="Modified"
                        render={renderModifiedDate}
                    />
                </DatagridConfigurable>
            </RaList>
        </>
    );
};

/**
 * RTP Performers Page configuration object
 * Follows the pattern expected by the React Admin resource system
 */
interface RtpPerformersPageConfig {
    name: string;
    list: React.FC;
}

const RTP_PerformersPage: RtpPerformersPageConfig = {
    name: 'RTP_Performers',
    list: RTP_Performers
};

export default RTP_PerformersPage;